<!-- ****************************************************
*     		               NOTE                      	*
* If you make changes to this component, check also:    *
* table cell: DetailButton                              *
* table cell: Download                                  *
* it may need some fixes there depending on changes made*
***************************************************** -->
<template>
    <button
    class="button-quaternary"
    :disabled="disabled"
    :class="[{'selected': selected}, buttonStyle, buttonSize, getAppThemeMode]"
    @click.stop="emit('clicked')">
        <!-- Button Icon -->
        <app-icon v-if="icon" class="icon"/>
        <!-- Button text -->
        <div v-else class="text">{{text}}</div>
    </button>
</template>

<script setup>
/* Vue */
import { computed, defineAsyncComponent } from 'vue'
import { iconModules } from '@/assets/icons/iconModules'

/* Store */
import { storeToRefs } from 'pinia'
import { useProjectBaseStore } from '@/store/projectBase'
const { getAppThemeMode } = storeToRefs(useProjectBaseStore())

/* Emmitters */
const emit = defineEmits(['clicked'])

/** Props received:
@text {String}
@icon {String}
@buttonStyle {String} can be [square (default), square-transparent, round, round-transparent]
@buttonSize {String} can be [small (default), tiny]
@disabled {Boolean}
*/
const props = defineProps({
    text: String,
	icon: String,
	buttonStyle: {
        type: String,
        default: 'square'
    },
    buttonSize: {
        type: String,
        default: 'small'
    },
    disabled: Boolean
})

/* Computed */
const appIcon = computed(() => {
    const icon = props.icon
	return defineAsyncComponent(iconModules[`/src/assets/icons/${icon}.svg`])
})
</script>

<style lang="scss" scoped>
.button-quaternary {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    @include H7-Medium;
    color: var(--secondary-dark-2);

    cursor: pointer;

    svg.icon {
        width: 16px;
        height: 16px;
        pointer-events: none;

        :deep(.change-color-fill) {
            fill: var(--secondary-dark-2);
        }
    }

    /* Style classes */
    &.square, &.square-transparent {
        border-radius: 8px;
    }

    &.round, &.round-transparent {
        border-radius: 50%;
    }

    /* Size classes */
    &.small {
        width: 40px;
        height: 40px;
    }

    &.tiny {
        width: 32px;
        height: 32px;
    }

    @media (hover: hover) and (pointer: fine) {
        &:hover {
            background-color: var(--button-secondary-state-hover);
        }
    }

    &:active {
        background-color: var(--button-secondary-state-press);
    }

	&.square-transparent, &.round-transparent {
		color: var(--secondary-light-1);

		svg.icon {
			:deep(.change-color-fill) {
				fill: var(--secondary-light-1);
			}
		}

		@media (hover: hover) and (pointer: fine) {
			&:hover {
				background-color: var(--button-quaternary-transparent-state-hover);
			}
		}

		&:active {
			background-color: var(--button-quaternary-transparent-state-press);
		}

		&.dark {
			color: var(--secondary-dark-2);

			svg.icon {
				:deep(.change-color-fill) {
					fill: var(--secondary-dark-2);
				}
			}
		}
	}

    &:disabled {
        cursor: default;
        pointer-events: none;
        opacity: 0.24;
    }
}
</style>
